import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { PaymentService } from '../../shared/services/payment.service';
import { AuthService } from '../../shared/services/auth.service';
import { forkJoin, Observable, firstValueFrom, first } from 'rxjs';
import { CommonModule, DatePipe, TitleCasePipe, NumberFormatStyle } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
// Import removed as it's not used in this component
import { Location } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { jwtDecode } from 'jwt-decode';
import { MobilePageShellComponent } from '../../shared/mobile/mobile-page-shell/mobile-page-shell.component';
import { MobileDataCardComponent } from '../../shared/mobile/mobile-data-card/mobile-data-card.component';
import { ConfigItemOffcanvasComponent, ConfigOffcanvasMode } from './config-item-offcanvas/config-item-offcanvas.component';
import { SectionCardComponent } from '../../shared/section-card/section-card.component';
import { ToastService } from '../../shared/services/toast.service';

interface User {
  userID: number,
  email: string,
  firstName: string,
  lastName: string,
  roleID: number,
  roleName?: string,
  isActive: boolean,
  createdAt: string,
  updatedAt: string,
  lastLogin?: string
}
interface Role {
  roleID: number,
  roleName: string,
  description: string,
  isAdmin: boolean,
  isActive: boolean,
  createdAt: string,
  updatedAt: string
}

interface Category {
  categoryID: number;
  categoryId?: number;  // Add both variants to handle API responses
  categoryName: string;
  description?: string;
  parentCategoryID?: number;
  parentCategoryId?: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  parentCategory?: Category;
  childCategories?: Category[];
}

interface Warehouse {
  warehouseID: number;
  warehouseCode: string;
  warehouseName: string;
  locationDescription: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface Subscription {
  id: number;
  tenantId: number;
  stripeSubscriptionId?: string;
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelledAt?: string;
  isCancelled: boolean;
  planName?: string;
  amount: number;
  currency?: string;
  // Paystack specific fields
  paystackSubscriptionCode?: string;
  paystackEmailToken?: string;
  paymentProvider?: 'stripe' | 'paystack';
}

interface PaymentRecord {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed';
  description?: string;
  type?: string;
  invoiceUrl?: string;
  receiptUrl?: string;
}

interface SupplierLocation {
  locationID: number;
  supplierID: number;
  branchName?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  country?: string;
  locationContactPerson?: string;
  locationEmail?: string;
  locationPhone?: string;
  isActive: boolean;
  isPhysical: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface Supplier {
  supplierID: number;
  companyName: string;
  primaryContactPerson?: string;
  primaryEmail?: string;
  primaryPhone?: string;
  website?: string;
  isActive: boolean;
  supplierCategoryID?: number | null;
  createdAt?: string;
  updatedAt?: string;
  locations?: SupplierLocation[];
}

interface SupplierCategory {
  supplierCategoryID: number;
  description: string;
  sageSupplierCategoryId?: string;
  createdAt?: string;
  updatedAt?: string;
}
@Component({
  selector: 'app-config',
  templateUrl: './config.component.html',
  styleUrls: ['./config.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MobilePageShellComponent,
    MobileDataCardComponent,
    ConfigItemOffcanvasComponent,
    SectionCardComponent
  ],
  providers: [DatePipe, TitleCasePipe],
  animations: [
    trigger('slideInOut', [
      state('void', style({
        height: '0',
        opacity: '0',
        overflow: 'hidden'
      })),
      state('*', style({
        height: '*',
        opacity: '1',
        overflow: 'hidden'
      })),
      transition('void <=> *', animate('200ms ease-in-out'))
    ])
  ]
})
export class ConfigComponent implements OnInit, AfterViewInit, OnDestroy {
  tenantName: any;
  fullName: any;
  isSendingWelcomeEmail = false;
  users: User[] = [];

  changeTab(tabId: string): void {
    this.activeTab = tabId;
    const tab = document.getElementById(`${tabId}-tab`);
    if (tab) {
      // Using Bootstrap's Tab API to programmatically switch tabs
      const tabInstance = new (window as any).bootstrap.Tab(tab);
      tabInstance.show();
    }
  }

  activeTab: string = 'users'; // Default active tab
  singleTabView = false;
  isSupplierView = false;
  isRoleView = false;


  // Modal elements
  // Modal visibility flags
  showUserModal = false;
  showPassword = false; // Add this line for password visibility toggle
  showRoleModal = false;
  showCategoryModal = false;
  showDeleteModal = false;
  mobileDeleteClosing = false;
  showPaymentModal = false;
  showSubscriptionModal = false;
  isProcessingPayment = false;
  isProcessingSubscription = false;


  suppliers: Supplier[] = [];
  supplierLocations: SupplierLocation[] = [];
  supplierCategories: SupplierCategory[] = [];
  showQuickAddCategory = false;
  newSupplierCategoryName = '';
  isSavingQuickAddCategory = false;
  isEditingSupplier = false;
  showSupplierModal = false;
  isEditingSupplierLocation = false;
  isEditingLocation = false; // For the location modal visibility

  // Add these form groups to the component class
  supplierForm!: FormGroup;
  supplierLocationForm!: FormGroup;


  subscriptionAction: 'cancel' | 'renew' | 'activate' = 'cancel';
  subscriptionActionTitle = '';
  subscriptionActionMessage = '';
  subscriptionActionButtonText = '';
  subscriptionActionButtonClass = '';

  userEmail: string = ''; // Add userEmail property
  currentUserId: number | null = null;
  currentUserEmail: string | null = null;

  clientSecret: string | null = null;

  // Subscription management properties
  errorMessage: string | null = null;
  isLoading = false;
  subscriptionCode: string | null = null;
  userRole: string | null = null;
  accountType: string = 'full';
  navigationSource: 'business-details' | 'inventory' | 'other' = 'other';
  // Provider (Paystack) snapshot
  providerStatus: string | null = null;
  providerNextPaymentDate: string | null = null;
  providerSubscriptionCode: string | null = null;
  providerEmailToken: string | null = null;
  private providerPollIntervalId: any = null;
  private providerPollTimeoutId: any = null;
  isPollingProviderStatus = false;
  private mobileDeleteClosingTimeoutId: any = null;
  private supplierClosingTimeoutId: any = null;
  private mobileResetClosingTimeoutId: any = null;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private paymentService: PaymentService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private toast: ToastService
  ) {
    // Track where the navigation is coming from
    const state = this.router.getCurrentNavigation()?.extras?.state;
    if (state && state['from'] === 'business-details') {
      this.navigationSource = 'business-details';
    } else if (state && state['from'] === 'inventory') {
      this.navigationSource = 'inventory';
    }
  }

  ngOnInit(): void {

    const JWT = localStorage.getItem('authToken');
    const decodedToken: any = jwtDecode(JWT!);
    this.tenantName = decodedToken.TenantName;
    this.fullName = decodedToken.FullName;
    // Track current user id from token for email resolution
    const rawUserId = decodedToken?.UserId ?? decodedToken?.userId;
    this.currentUserId = rawUserId ? Number(rawUserId) : null;
    this.userRole = this.authService.getUserRole();
    this.accountType = this.authService.getAccountType();
    if (this.accountType === 'calculator') {
      this.activeTab = 'subscription';
    }
// $&
    this.route.queryParams.subscribe(params => {
      const tab = params['tab'];
      const view = params['view'];

      if (tab && this.isValidTab(tab)) {
        this.activeTab = tab;
        // Programmatically show the tab
        setTimeout(() => this.changeTab(tab), 0);
      }

      if (tab === 'suppliers' && view === 'single') {
        this.singleTabView = true;
        this.isSupplierView = true;
      }
      if (tab === 'roles' && view === 'single') {
        this.singleTabView = true;
        this.isRoleView = true;
      }
    });

    this.initForms();
    this.loadData();
    this.loadSuppliers();
    this.loadSupplierCategories();

  }

  async ngAfterViewInit(): Promise<void> {
    this.initializeModals();
    // Change tab after view is initialized to ensure elements are available
    if (this.activeTab) {
      this.changeTab(this.activeTab);
    }
    // Setup Stripe but don't mount elements yet

  }

  ngOnDestroy(): void {
    // Reset overlay flags so no fixed-position overlay lingers after navigation
    this.showDeleteModal = false;
    this.mobileDeleteClosing = false;
    this.supplierIsClosing = false;
    this.mobileResetPwdVisible = false;
    this.mobileResetIsClosing = false;

    if (this.mobileDeleteClosingTimeoutId) clearTimeout(this.mobileDeleteClosingTimeoutId);
    if (this.supplierClosingTimeoutId) clearTimeout(this.supplierClosingTimeoutId);
    if (this.mobileResetClosingTimeoutId) clearTimeout(this.mobileResetClosingTimeoutId);
    if (this.providerPollIntervalId) clearInterval(this.providerPollIntervalId);
    if (this.providerPollTimeoutId) clearTimeout(this.providerPollTimeoutId);
  }

  private isValidTab(tabId: string): boolean {
    const validTabs = ['users', 'roles', 'categories', 'suppliers', 'subscription'];
    return validTabs.includes(tabId);
  }





  // Toggle password visibility
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  // Show alert message
  private showAlert(title: string, message: string, type: 'success' | 'error' | 'warning' = 'success'): void {
    this.toast.show(type, message, 5000);
  }

  // Data properties
  roles: Role[] = [];
  categories: Category[] = [];
  warehouses: Warehouse[] = [];
  subscriptions: Subscription[] = [];
  paymentHistory: PaymentRecord[] = [];
  isLoadingPaymentHistory = false;

  // Form groups with definite assignment assertion
  userForm!: FormGroup;
  roleForm!: FormGroup;
  categoryForm!: FormGroup;
  paymentForm!: FormGroup;
  warehouseForm!: FormGroup;
  subscriptionForm!: FormGroup;
  isEditingUser = false;
  isEditingRole = false;
  isEditingCategory = false;
  isEditingWarehouse = false;
  itemToDelete: any = null;
  selectedCategory: Category | null = null;
  deleteModal: any;
  userModal: any;
  roleModal: any;
  warehouseModal: any;
  deleteModalTitle: string = 'Confirm Delete';
  currentSort = {
    field: 'roleName',
    direction: 'asc'
  };

  // Get user count for a specific role
  getUserCount(roleId: number): number {
    return this.users?.filter(u => u.roleID === roleId).length || 0;
  }

  // Initialize all forms
  private initForms(): void {
    this.initUserForm();
    this.initRoleForm();
    this.initCategoryForm();
    this.initSupplierForm();
    this.initSupplierLocationForm();
    this.initPaymentForm();
  }

  private initUserForm(): void {
    this.userForm = this.fb.group({
      userID: [null],
      email: ['', [Validators.required, Validators.email]],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      roleID: [null, [Validators.required]],
      isActive: [true],
      password: ['', []] // No validators here - we'll handle it dynamically
    });

    // Set up password validation based on edit mode
    this.setupPasswordValidation();
  }

  private initRoleForm(): void {
    this.roleForm = this.fb.group({
      roleID: [null],
      roleName: ['', Validators.required],
      description: [''],
      isAdmin: [false],
      isActive: [true],
      createdAt: [''],
      updatedAt: ['']
    });
  }

  private initCategoryForm(): void {
    this.categoryForm = this.fb.group({
      categoryId: [0],
      categoryName: ['', Validators.required],
      createdAt: [new Date().toISOString()]
    });
  }

  private initPaymentForm(): void {
    this.paymentForm = this.fb.group({
      cardNumber: ['', [
        Validators.required,
        Validators.pattern(/^[0-9]{13,19}$/)
      ]],
      expiryDate: ['', [
        Validators.required,
        Validators.pattern(/^(0[1-9]|1[0-2])\/([0-9]{2})$/)
      ]],
      cvv: ['', [
        Validators.required,
        Validators.pattern(/^[0-9]{3,4}$/)
      ]],
      cardholderName: ['', Validators.required]
    });
  }

  private initializeModals(): void {
    // No need to initialize Bootstrap modals directly
  }

  private loadData(): void {
    this.loadUsers();
    this.loadRoles();
    this.loadCategories();
    this.loadSubscriptions();
    // this.loadPaymentHistory();
    // this.loadWarehouses();
  }

  loadSubscriptions(): void {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });

    this.http.get<Subscription>(`${environment.apiUrl}/api/Subscription/GetCurrent`, { headers })
      .subscribe({
        next: (subscription: Subscription) => {
          if (subscription) {
            this.subscriptions = [subscription];
          } else {
            this.subscriptions = [];
          }
          // Only fetch Paystack provider status when the tenant actually has a Paystack subscription
          if (subscription?.paymentProvider === 'paystack' && subscription?.paystackSubscriptionCode) {
            this.loadProviderSubscription();
          }
        },
        error: (error) => {
// $&
          this.showAlert('Error', 'Failed to load subscription information. Please try again later.', 'error');
        }
      });
  }

  // Fetch Paystack subscription details (status, next payment date) to drive Manage vs Renew buttons
  loadProviderSubscription(): void {
    const token = this.authService.getToken();
    if (!token) { return; }
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });
    this.http.get<any>(`${environment.apiUrl}/api/Subscription/paystack/subscription`, { headers })
      .subscribe({
        next: (res) => {
          const data = res?.data || {};
          this.providerStatus = (data.providerStatus || '').toLowerCase();
          this.providerNextPaymentDate = data.nextPaymentDate || null;
          this.providerSubscriptionCode = data.subscriptionCode || null;
          this.providerEmailToken = data.emailToken || null;
          // If polling and provider is no longer active, stop polling so UI flips to Renew
          if (this.isPollingProviderStatus && this.providerStatus && this.providerStatus !== 'active') {
            this.stopProviderStatusPolling();
          }
        },
        error: () => {
          // Non-blocking; provider details may be unavailable during trial or initial setup
        }
      });
  }

  // Helper: is provider indicating coverage in future
  private isProviderFutureDated(): boolean {
    if (!this.providerNextPaymentDate) return false;
    const next = new Date(this.providerNextPaymentDate).getTime();
    return !isNaN(next) && next > Date.now();
  }

  // Label for the Renew/Change Plan button to avoid confusing wording for active payers
  getRenewButtonLabel(): string {
    const provider = (this.providerStatus || '').toLowerCase();
    if (provider === 'active') return 'Change Plan';

    // Fallback to local if provider unknown
    const localStatus = (this.subscriptions && this.subscriptions[0]?.status || '').toLowerCase();
    if (localStatus === 'active') return 'Change Plan';

    return 'Renew Subscription';
  }

  // Begin polling provider status (used after opening manage link so we reflect cancellations)
  private startProviderStatusPolling(totalMs: number = 180000, intervalMs: number = 4000): void {
    this.stopProviderStatusPolling();
    this.isPollingProviderStatus = true;

    // Initial fetch immediately
    this.loadProviderSubscription();

    this.providerPollIntervalId = setInterval(() => {
      this.loadProviderSubscription();
    }, intervalMs);

    this.providerPollTimeoutId = setTimeout(() => {
      this.stopProviderStatusPolling();
    }, totalMs);
  }

  private stopProviderStatusPolling(): void {
    if (this.providerPollIntervalId) {
      clearInterval(this.providerPollIntervalId);
      this.providerPollIntervalId = null;
    }
    if (this.providerPollTimeoutId) {
      clearTimeout(this.providerPollTimeoutId);
      this.providerPollTimeoutId = null;
    }
    this.isPollingProviderStatus = false;
  }

  // loadPaymentHistory(): void {
  //   this.isLoadingPaymentHistory = true;
  //   const token = localStorage.getItem('authToken');
  //   const headers = new HttpHeaders({
  //     'Content-Type': 'application/json',
  //     Authorization: `Bearer ${token}`
  //   });

  //   this.http.get<PaymentRecord[]>(`${environment.apiUrl}/api/Subscription/GetPaymentHistory`, { headers })
  //     .subscribe({
  //       next: (payments: PaymentRecord[]) => {
  //         this.paymentHistory = payments;
  //       },
  //       error: (error) => {
  //         console.error('Error loading payment history:', error);
  //         this.showAlert('Error', 'Failed to load payment history. Please try again later.', 'error');
  //       },
  //       complete: () => {
  //         this.isLoadingPaymentHistory = false;
  //       }
  //     });
  // }

  sortSubscriptions(field: string): void {
    const direction = this.currentSort.field === field && this.currentSort.direction === 'asc' ? 'desc' : 'asc';

    this.subscriptions.sort((a: any, b: any) => {
      const valueA = a[field] !== null ? a[field].toString().toLowerCase() : '';
      const valueB = b[field] !== null ? b[field].toString().toLowerCase() : '';

      if (valueA < valueB) {
        return direction === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    this.currentSort = { field, direction };
  }

  showSubscriptionConfirmation(action: 'cancel' | 'renew' | 'activate'): void {
    this.subscriptionAction = action;

    if (action === 'cancel') {
      this.subscriptionActionTitle = 'Cancel Subscription';
      this.subscriptionActionMessage = 'Are you sure you want to cancel your subscription?';
      this.subscriptionActionButtonText = 'Yes, Cancel';
      this.subscriptionActionButtonClass = 'btn-danger';
    } else if (action === 'renew') {
      this.subscriptionActionTitle = 'Renew Subscription';
      this.subscriptionActionMessage = 'Are you sure you want to renew your subscription?';
      this.subscriptionActionButtonText = 'Yes, Renew';
      this.subscriptionActionButtonClass = 'btn-success';
    } else if (action === 'activate') {
      this.subscriptionActionTitle = 'Activate Subscription';
      this.subscriptionActionMessage = 'Are you sure you want to reactivate your subscription?';
      this.subscriptionActionButtonText = 'Yes, Activate';
      this.subscriptionActionButtonClass = 'btn-success';
    }

    this.showSubscriptionModal = true;
  }

  async confirmSubscriptionAction(): Promise<void> {
    this.isProcessingSubscription = true;
    let actionRequest: Observable<any>;
    let successMessage = '';

    try {
      const currentSubscription = this.subscriptions[0];
      const isPaystack = currentSubscription?.paymentProvider === 'paystack' ||
                        (environment.paystackPublicKey && !currentSubscription?.paymentProvider);

      switch (this.subscriptionAction) {
        case 'cancel':
          if (isPaystack && currentSubscription?.paystackSubscriptionCode && currentSubscription?.paystackEmailToken) {
            actionRequest = this.paymentService.cancelSubscription(
              currentSubscription.paystackSubscriptionCode,
              currentSubscription.paystackEmailToken
            );
          } else {
            actionRequest = this.paymentService.cancelSubscription();
          }
          successMessage = 'Your subscription has been cancelled successfully.';
          break;
        case 'renew':
          if (isPaystack && currentSubscription?.paystackSubscriptionCode && currentSubscription?.paystackEmailToken) {
            actionRequest = this.paymentService.renewSubscription(
              currentSubscription.paystackSubscriptionCode,
              currentSubscription.paystackEmailToken
            );
          } else {
            actionRequest = this.paymentService.renewSubscription();
          }
          successMessage = 'Your subscription has been renewed successfully.';
          break;
        case 'activate':
          if (isPaystack && currentSubscription?.paystackSubscriptionCode && currentSubscription?.paystackEmailToken) {
            // For Paystack, activation is the same as renewal (enable)
            actionRequest = this.paymentService.renewSubscription(
              currentSubscription.paystackSubscriptionCode,
              currentSubscription.paystackEmailToken
            );
          } else {
            actionRequest = this.paymentService.activateSubscription();
          }
          successMessage = 'Your subscription has been activated successfully.';
          break;
        default:
          this.isProcessingSubscription = false;
          return;
      }

      actionRequest.subscribe({
        next: () => {
          this.showAlert('Success', successMessage, 'success');
          this.loadSubscriptions();
          this.closeSubscriptionModal();
        },
        error: (error) => {
          const errorMessage = error.error?.message || `Failed to ${this.subscriptionAction} subscription. Please try again.`;
          this.showAlert('Error', errorMessage, 'error');
// $&
        },
        complete: () => {
          this.isProcessingSubscription = false;
        }
      });
    } catch (error) {
// $&
      this.showAlert('Error', 'An unexpected error occurred. Please try again.', 'error');
      this.isProcessingSubscription = false;
    }
  }

  closeSubscriptionModal(): void {
    this.showSubscriptionModal = false;
    this.isProcessingSubscription = false;
  }

  // Navigate to the renewal flow with the user's email
  renewSubscriptionNow(): void {
    // 1) Try AuthService decoded token claim
    let email = this.authService.getCurrentUserEmail() || '';

    // 2) Fallback to cached resolution from users list
    if (!email) {
      email = this.currentUserEmail || '';
    }

    // 3) Fallback to remembered email from login
    if (!email) {
      email = localStorage.getItem('rememberedEmail') || '';
    }

    // 4) As a last resort prompt the user
    if (!email) {
      const entered = window.prompt('Enter the email you used for your subscription renewal:');
      if (entered && entered.includes('@')) {
        email = entered.trim();
      }
    }

    if (!email) {
      this.showAlert('Renewal', 'Email is required for renewal. Please enter your email on the login page and click Renew.', 'warning');
      return;
    }

    this.router.navigate(['/renewal'], { state: { email } });
  }


  closePaymentModal(): void {
    this.showPaymentModal = false;
    this.paymentForm.reset();
  }

  loadUsers(): void {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });

    forkJoin([
      this.http.get<User[]>(`${environment.apiUrl}/api/Users/GetAll`, { headers }),
      this.http.get<Role[]>(`${environment.apiUrl}/api/Role/GetAll`, { headers })
    ]).subscribe({
      next: ([users, roles]: [User[], Role[]]) => {
        const roleMap = new Map<number, string>();
        roles.forEach((role: Role) => roleMap.set(role.roleID, role.roleName));

        this.users = users.map((user: User) => ({
          ...user,
          roleName: roleMap.get(user.roleID) || 'Unknown Role'
        }));

        // Resolve current user's email from user list using token claim
        if (this.currentUserId) {
          const me = this.users.find(u => u.userID === this.currentUserId);
          this.currentUserEmail = me?.email || null;
        }

        this.sortUsers('username');
      },
      error: (error) => console.error('Error loading data:', error)
    });
  }

  loadRoles(): void {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });
    this.http.get<Role[]>(`${environment.apiUrl}/api/Role/GetAll`, { headers }).subscribe({
      next: (roles) => {
        this.roles = roles;
        this.sortRoles('roleName');
// $&
      },
      error: (error) => console.error('Error loading roles:', error)
    });
  }

  loadCategories(): void {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'accept': '*/*',
      'Authorization': `Bearer ${token}`
    });

    this.http.get<Category[]>(`${environment.apiUrl}/api/Category/GetAll`, { headers })
      .subscribe({
        next: (categories) => {
          this.categories = categories;
        },
        error: (error) => console.error('Error loading categories:', error)
      });
  }

  loadSupplierCategories(): void {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'accept': '*/*',
      'Authorization': `Bearer ${token}`
    });

    this.http.get<SupplierCategory[]>(`${environment.apiUrl}/api/SupplierCategory/GetAll`, { headers })
      .subscribe({
        next: (supplierCategories) => {
          this.supplierCategories = supplierCategories;
        },
        error: (error) => console.error('Error loading supplier categories:', error)
      });
  }

  toggleQuickAddCategory(show: boolean): void {
    this.showQuickAddCategory = show;
    if (!show) {
      this.newSupplierCategoryName = '';
    }
  }

  quickAddSupplierCategory(): void {
    const description = this.newSupplierCategoryName.trim();
    if (!description || this.isSavingQuickAddCategory) {
      return;
    }

    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });

    this.isSavingQuickAddCategory = true;
    this.http.post<SupplierCategory>(`${environment.apiUrl}/api/SupplierCategory/Add`, { description }, { headers })
      .subscribe({
        next: (savedCategory) => {
          this.supplierCategories = [...this.supplierCategories, savedCategory];
          this.supplierForm.patchValue({ supplierCategoryID: savedCategory.supplierCategoryID });
          this.isSavingQuickAddCategory = false;
          this.toggleQuickAddCategory(false);
        },
        error: (error) => {
          this.isSavingQuickAddCategory = false;
          this.showAlert('Error', 'Failed to add category: ' + (error.error?.message || error.error || 'Unknown error'), 'error');
        }
      });
  }

  private setupPasswordValidation(): void {
    const passwordControl = this.userForm.get('password');
    if (passwordControl) {
      passwordControl.clearValidators();
      passwordControl.updateValueAndValidity();
    }
  }

  private generateTempPassword(length: number = 12): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*()_+-=';
    let pwd = '';
    for (let i = 0; i < length; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pwd;
  }

  showAddUserModal(): void {
    this.isEditingUser = false;
    this.showUserModal = true;

    this.userForm.reset({
      isActive: true,
      password: ''
    });

    this.setupPasswordValidation();

    this.userForm.markAsPristine();
    this.userForm.markAsUntouched();
  }

  closeUserModal(): void {
    this.showUserModal = false;
    this.selectedUser = null;
  }

  selectedUser: User | null = null;

  editUser(user: User): void {
    this.isEditingUser = true;
    this.selectedUser = { ...user };
    this.showUserModal = true;

    this.userForm.reset({
      userID: user.userID,
      email: user.email,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      roleID: user.roleID,
      isActive: user.isActive !== false,
      password: ''
    });

    this.setupPasswordValidation();
    this.userForm.markAsDirty();
  }

  isFormValid(): boolean {
    return !!(
      this.userForm.get('email')?.valid &&
      this.userForm.get('firstName')?.valid &&
      this.userForm.get('lastName')?.valid &&
      this.userForm.get('roleID')?.valid
    );
  }

  saveUser(): void {
    this.userForm.markAllAsTouched();

    if (this.isEditingUser && !this.userForm.value.password) {
      const passwordControl = this.userForm.get('password');
      if (passwordControl) {
        passwordControl.setErrors(null);
      }
    }

    if (!this.isFormValid()) {
// $&
      return;
    }

    // Client-side duplicate email check (case-insensitive)
    const emailCtrl = this.userForm.get('email');
    const enteredEmail = (emailCtrl?.value || '').trim().toLowerCase();
    const isDuplicate = this.users?.some(u =>
      (u.email || '').trim().toLowerCase() === enteredEmail && (!this.isEditingUser || u.userID !== this.userForm.value.userID)
    );

    if (isDuplicate) {
      const currentErrors = emailCtrl?.errors || {};
      emailCtrl?.setErrors({ ...currentErrors, duplicate: true });
      emailCtrl?.markAsTouched();
      this.showAlert('Duplicate email', 'A user with this email already exists. Please use a different email.', 'warning');
      return;
    } else if (emailCtrl?.hasError('duplicate')) {
      // Clear duplicate error if it was previously set and is no longer applicable
      const { duplicate, ...rest } = emailCtrl.errors || {};
      emailCtrl.setErrors(Object.keys(rest).length ? rest : null);
    }

    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'accept': '*/*',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    const now = new Date().toISOString();

    // Ensure a password exists for new users; generate one if needed so the email matches the created credentials
    let finalPassword: string | undefined = this.userForm.value.password;
    if (!this.isEditingUser && (!finalPassword || finalPassword.trim() === '')) {
      finalPassword = this.generateTempPassword();
      this.userForm.patchValue({ password: finalPassword });
    }

    const userData = {
      userID: this.isEditingUser ? this.userForm.value.userID : 0,
      username: this.userForm.value.email,
      passwordHash: finalPassword || this.userForm.value.password || 'string',
      email: this.userForm.value.email || 'string@wwww',
      firstName: this.userForm.value.firstName || 'string',
      lastName: this.userForm.value.lastName || 'string',
      roleID: this.userForm.value.roleID,
      isActive: this.userForm.value.isActive !== false,
      createdAt: this.isEditingUser ? this.userForm.value.createdAt : now,
      updatedAt: now,
      lastLogin: this.isEditingUser ? this.userForm.value.lastLogin : now
    };

    const request = this.isEditingUser
      ? this.http.put(`${environment.apiUrl}/api/Users/Update?UsersId=${this.userForm.value.userID}`, userData, { headers })
      : this.http.post(`${environment.apiUrl}/api/Users/Add`, userData, { headers });

    request.subscribe({
      next: () => {
        this.loadUsers();

        // Only send welcome email on user creation, not on edit
        if (!this.isEditingUser) {
          const emailPayload = {
            email: this.userForm.value.email,
            firstName: this.userForm.value.firstName,
            lastName: this.userForm.value.lastName,
            temporaryPassword: finalPassword || this.userForm.value.password || '',
            tenantName: this.tenantName
          };

          this.isSendingWelcomeEmail = true;
          this.http.post(`${environment.apiUrl}/api/Tenant/send-welcome-email`, emailPayload, { headers })
            .subscribe({
              next: () => {
                this.showAlert('Success', 'Welcome email sent.', 'success');
              },
              error: (err) => {
// $&
                this.showAlert('Notice', 'User added, but failed to send welcome email.', 'warning');
              },
              complete: () => {
                this.isSendingWelcomeEmail = false;
                this.closeUserModal();
              }
            });
        } else {
          this.showAlert('Success', 'User updated successfully.', 'success');
          this.closeUserModal();
        }
      },
      error: (error) => console.error('Error saving user:', error)
    });
  }

  showAddRoleModal(): void {
    this.isEditingRole = false;
    this.roleForm.reset({
      roleID: null,
      roleName: '',
      description: '',
      isAdmin: false,
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    this.showRoleModal = true;
  }

  closeRoleModal(): void {
    this.showRoleModal = false;
  }

  editRole(role: Role): void {
    this.isEditingRole = true;
    this.roleForm.patchValue({
      roleID: role.roleID,
      roleName: role.roleName,
      description: role.description || '',
      isAdmin: role.isAdmin || false,
      isActive: role.isActive,
      createdAt: role.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    this.showRoleModal = true;
  }

  saveRole(): void {
    if (this.roleForm.invalid) return;

    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'accept': '*/*',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    const now = new Date().toISOString();
    const roleData = {
      roleID: this.isEditingRole ? this.roleForm.value.roleID : 0,
      roleName: this.roleForm.value.roleName,
      description: this.roleForm.value.description || '',
      isAdmin: this.roleForm.value.isAdmin || false,
      isActive: this.roleForm.value.isActive !== undefined ? this.roleForm.value.isActive : true,
      createdAt: this.isEditingRole ? this.roleForm.value.createdAt : now,
      updatedAt: now
    };

    const request = this.isEditingRole
      ? this.http.put(`${environment.apiUrl}/api/Role/Update?RoleId=${roleData.roleID}`, roleData, { headers })
      : this.http.post(`${environment.apiUrl}/api/Role/Add`, roleData, { headers });

    request.subscribe({
      next: () => {
        this.loadRoles();
        this.closeRoleModal();
        this.showAlert('Success', `Role ${this.isEditingRole ? 'updated' : 'created'} successfully`, 'success');
      },
      error: (error) => {
// $&
        this.showAlert('Error', `Failed to ${this.isEditingRole ? 'update' : 'create'} role`, 'error');
      }
    });
  }

  showDeleteConfirmation(item: any, type: string): void {
// $&
    this.itemToDelete = { item, type };
    this.deleteModalTitle = 'Confirm Delete';
    this.showDeleteModal = true;
    this.deleteModalTitle = type.toLowerCase() === 'user' ? 'Confirm Deactivate' : 'Confirm Delete';
  }

  confirmDeleteUser(user: User): void {
    this.showDeleteConfirmation(user, 'user');
  }

  confirmDeleteRole(role: Role): void {
    this.showDeleteConfirmation(role, 'role');
  }

  confirmDeleteCategory(category: Category): void {
    this.showDeleteConfirmation(category, 'category');
  }
  confirmDeleteSupplierLocation(location: SupplierLocation): void {
    this.showDeleteConfirmation(location, 'supplierLocation');
  }
  confirmDeleteSupplier(supplier: Supplier): void {
    this.showDeleteConfirmation(supplier, 'supplier');
  }

  async deactivateUser(userId: number): Promise<boolean> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        this.showAlert('Error', 'Authentication token not found', 'error');
        return false;
      }

      const headers = new HttpHeaders({
        'accept': '*/*',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      });

      const userResponse: any = await this.http.get(`${environment.apiUrl}/api/Users/GetById?UsersId=${userId}`, { headers }).toPromise();

      if (!userResponse) {
        throw new Error('User not found');
      }

      const userData = {
        ...userResponse,
        isActive: false,
        updatedAt: new Date().toISOString()
      };

      await this.http.put(`${environment.apiUrl}/api/Users/Update?UsersId=${userId}`, userData, { headers }).toPromise();
      return true;
    } catch (error) {
// $&
      return false;
    }
  }

  async deleteItem(id: number | undefined, type: string): Promise<void> {
    if ((id === undefined || id === null) && this.itemToDelete?.item) {
      const item = this.itemToDelete.item;
      id = item.supplierID || item.categoryID || item.categoryId || item.userID || item.roleID || item.id || item.ID || item.supplierId || item.locationId;
      type = this.itemToDelete.type || type;
// $&
    }

    if (id === undefined || id === null) {
      console.error('Cannot process item: ID is undefined', {
        itemToDelete: this.itemToDelete,
        providedId: id,
        providedType: type
      });
      this.showAlert('Error', 'Cannot process item: Invalid ID', 'error');
      return;
    }

    const isUser = type.toLowerCase() === 'user';

    if (isUser) {
      const success = await this.deactivateUser(id);
      if (success) {
        this.loadUsers();
        this.closeDeleteModal();
        this.showAlert('Success', 'User deactivated successfully', 'success');
      } else {
        this.showAlert('Error', 'Failed to deactivate user', 'error');
      }
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      this.showAlert('Error', 'Authentication token not found', 'error');
      return;
    }

    const headers = new HttpHeaders({
      'accept': '*/*',
      'Authorization': `Bearer ${token}`
    });

    let endpoint = '';
    const isRole = type.toLowerCase() === 'role';
    const isCategory = type.toLowerCase() === 'category';
    const isWarehouse = type.toLowerCase() === 'warehouse';
    const isSupplier = type.toLowerCase() === 'supplier';
    const isLocation = type.toLowerCase() === 'location';

    if (isRole) {
      endpoint = `${environment.apiUrl}/api/Role/Delete?RoleId=${id}`;
    } else if (isCategory) {
      endpoint = `${environment.apiUrl}/api/Category/Delete?CategoryId=${id}`;
    } else if (isWarehouse) {
      endpoint = `${environment.apiUrl}/api/Warehouses/Delete?WarehouseId=${id}`;
    } else if (isSupplier) {
      endpoint = `${environment.apiUrl}/api/Suppliers/Delete?SuppliersId=${id}`;
    } else if (isLocation) {
      endpoint = `${environment.apiUrl}/api/SupplierLocations/Delete?SupplierLocationsId=${id}`;
    } else {

      this.showAlert('Error', `Unknown item type: ${type}`, 'error');
      return;
    }



    this.http.delete(endpoint, { headers }).subscribe({
      next: (response) => {


        if (isRole) {
          this.loadRoles();
        } else if (isCategory) {
          this.loadCategories();
        } else if (isWarehouse) {
          // this.loadWarehouses();
        } else if (isSupplier) {
          this.loadSuppliers();
        }

        this.closeDeleteModal();
        this.showAlert('Success', `${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`, 'success');
      },
      error: (err) => {
        // Log the raw error for debugging purposes
        console.error('Failed to delete entity', { type, id, err });
        // Extract a readable message from the backend response
        const backendMessage: string = typeof err?.error === 'string' ? err.error : (err?.error?.message || err?.message || '');
        // Detect common FK/constraint messages from EF Core/MySQL
        const isFkViolation = /constraint|foreign key|FK_Items_Categories|saving the entity changes/i.test(backendMessage);
        if (isCategory && (err?.status === 400 || err?.status === 409 || isFkViolation)) {
          this.showAlert('Error', 'Category cannot be deleted because it is assigned to one or more items. Please reassign or remove those items first.', 'error');
        } else {
          const msg = backendMessage || `Failed to delete ${type}`;
          this.showAlert('Error', msg, 'error');
        }
      }
    });
  }

  showAddCategoryModal(): void {
    this.isEditingCategory = false;
    this.selectedCategory = null;
    this.categoryForm.reset({
      categoryId: 0,
      categoryName: '',
      createdAt: new Date().toISOString()
    });
    this.showCategoryModal = true;
  }

  closeCategoryModal(): void {
    this.showCategoryModal = false;
  }

  editCategory(category: Category): void {
// $&
    this.isEditingCategory = true;
    this.selectedCategory = { ...category };

    const categoryId = category.categoryID || category.categoryId;
    if (!categoryId) {
// $&
      this.showAlert('Error', 'Invalid category data. Please try again.', 'error');
      return;
    }

    this.selectedCategory.categoryID = categoryId;
    if (!this.selectedCategory.categoryId) {
      this.selectedCategory.categoryId = categoryId;
    }

    const formValues = {
      categoryId: categoryId,
      categoryName: category.categoryName,
      createdAt: category.createdAt || new Date().toISOString()
    };

// $&
    this.categoryForm.patchValue(formValues);
    this.showCategoryModal = true;
  }

  saveCategory(): void {
// $&

    if (this.categoryForm.invalid) {
// $&
      Object.keys(this.categoryForm.controls).forEach(key => {
        const control = this.categoryForm.get(key);
        if (control?.errors) {
// $&
        }
      });
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
// $&
      this.showAlert('Error', 'Authentication required', 'error');
      return;
    }

    const formValue = this.categoryForm.value;

    let categoryID = 0;
    if (this.isEditingCategory) {
      if (this.selectedCategory?.categoryID) {
        categoryID = Number(this.selectedCategory.categoryID);
// $&
      } else if (formValue.categoryId) {
        categoryID = Number(formValue.categoryId);
// $&
      }

      if (!categoryID) {
// $&
        this.showAlert('Error', 'Category ID is missing. Please refresh the page and try again.', 'error');
        return;
      }

      this.categoryForm.patchValue({ categoryId: categoryID }, { emitEvent: false });
    }

    const headers = new HttpHeaders({
      'accept': '*/*',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    const url = this.isEditingCategory
      ? `${environment.apiUrl}/api/Category/Update?CategoryId=${categoryID}`
      : `${environment.apiUrl}/api/Category/Add`;

    const requestBody = this.isEditingCategory
      ? {
          categoryID: categoryID,
          categoryName: formValue.categoryName
        }
      : {
          categoryName: formValue.categoryName,
          createdAt: new Date().toISOString()
        };

// $&
// $&

    const request = this.isEditingCategory
      ? this.http.put(url, requestBody, { headers })
      : this.http.post(url, requestBody, { headers });

    request.subscribe({
      next: () => {
// $&
        this.loadCategories();
        this.closeCategoryModal();
        this.showAlert('Success', `Category ${this.isEditingCategory ? 'updated' : 'added'} successfully`, 'success');
      },
      error: (error) => {
// $&
        const errorMessage = (typeof error.error === 'string' ? error.error : '') ||
                            error.error?.message ||
                            error.error?.title ||
                            'Failed to save category. Please try again.';
        this.showAlert('Error', errorMessage, 'error');
      }
    });
  }

  showAddWarehouseModal(): void {
    this.isEditingWarehouse = false;
    this.warehouseForm.reset({
      isActive: true
    });
    this.warehouseModal.show();
  }

  editWarehouse(warehouse: Warehouse): void {
    this.isEditingWarehouse = true;
    this.warehouseForm.patchValue({
      ...warehouse
    });
    this.warehouseModal.show();
  }

  saveWarehouse(): void {
    if (this.warehouseForm.invalid) return;

    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'accept': '*/*',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    const warehouseData = this.warehouseForm.value;
    const request = this.isEditingWarehouse
      ? this.http.put(`${environment.apiUrl}/api/Warehouses/Update?WarehousesId=${warehouseData.warehouseID}`, warehouseData, { headers })
      : this.http.post(`${environment.apiUrl}/api/Warehouses/Add`, warehouseData, { headers });

    request.subscribe({
      next: () => {
        // this.loadWarehouses();
        this.warehouseModal.hide();
      },
      error: (error) => console.error('Error saving warehouse:', error)
    });
  }

  confirmDeleteWarehouse(warehouse: Warehouse): void {
    this.showDeleteConfirmation(warehouse, 'warehouse');
  }

  closeDeleteModal(): void {
    this.mobileDeleteClosing = true;
    this.mobileDeleteClosingTimeoutId = setTimeout(() => {
      this.showDeleteModal = false;
      this.mobileDeleteClosing = false;
      this.itemToDelete = null;
      this.mobileDeleteSwipeDeltaY = 0;
      this.mobileDeleteSwipeProgress = 0;
      this.mobileDeleteSwipeActive = false;
    }, 300);
  }

  sortUsers(field: string): void {
    if (this.currentSort.field === field) {
      this.currentSort.direction = this.currentSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSort.field = field;
      this.currentSort.direction = 'asc';
    }

    this.users.sort((a: any, b: any) => {
      const valueA = a[field] || '';
      const valueB = b[field] || '';

      if (valueA < valueB) {
        return this.currentSort.direction === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.currentSort.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  sortRoles(field: string): void {
    if (this.currentSort.field === field) {
      this.currentSort.direction = this.currentSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSort.field = field;
      this.currentSort.direction = 'asc';
    }

    this.roles.sort((a: any, b: any) => {
      const aValue = a[field];
      const bValue = b[field];

      if (aValue === bValue) return 0;

      const direction = this.currentSort.direction === 'asc' ? 1 : -1;
      return aValue > bValue ? direction : -direction;
    });
  }

  sortCategories(field: string): void {
    if (this.currentSort.field === field) {
      this.currentSort.direction = this.currentSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSort.field = field;
      this.currentSort.direction = 'asc';
    }

    this.categories.sort((a: any, b: any) => {
      let aValue, bValue;

      if (field === 'parentCategory') {
        aValue = a.parentCategory?.categoryName || '';
        bValue = b.parentCategory?.categoryName || '';
      } else {
        aValue = a[field] || '';
        bValue = b[field] || '';
      }

      if (aValue === bValue) return 0;

      const direction = this.currentSort.direction === 'asc' ? 1 : -1;
      return aValue > bValue ? direction : -direction;
    });
  }

  private initSupplierForm(includeDefaultLocation: boolean = false): void {
    const locationsArray = includeDefaultLocation
      ? this.fb.array([this.createLocationFormGroup()])
      : this.fb.array([]);

    this.supplierForm = this.fb.group({
      supplierID: [null],
      companyName: ['', Validators.required],
      primaryContactPerson: [''],
      primaryEmail: ['', [Validators.email]],
      primaryPhone: [''],
      website: [''],
      isActive: [true],
      supplierCategoryID: [null],
      locations: locationsArray
    });
  }

  private createLocationFormGroup(): FormGroup {
    return this.fb.group({
      locationID: [null],
      branchName: ['Main Branch', Validators.required],
      addressLine1: [''],
      addressLine2: [''],
      city: [''],
      region: [''],
      postalCode: [''],
      country: [''],
      locationContactPerson: [''],
      locationEmail: [''],
      locationPhone: [''],
      isActive: [true],
      isPhysical: [true]
    });
  }

  /**
   * Gets the location form group at the specified index from the locations form array
   * @param index The index of the location form group to retrieve
   * @returns The form group at the specified index or null if not found
   */
  getLocationFormGroup(index: number): FormGroup | null {
    const locationsArray = this.supplierForm?.get('locations') as FormArray;
    if (!locationsArray || index >= locationsArray.length) {
      return null;
    }
    return locationsArray.at(index) as FormGroup;
  }

  private initSupplierLocationForm(): void {
    this.supplierLocationForm = this.fb.group({
      locationID: [null],
      supplierID: [null, Validators.required],
      branchName: [''],
      addressLine1: ['', Validators.required],
      addressLine2: [''],
      city: ['', Validators.required],
      region: [''],
      postalCode: [''],
      country: ['', Validators.required],
      locationContactPerson: [''],
      locationEmail: ['', [Validators.email]],
      locationPhone: [''],
      isActive: [true],
      isPhysical: [true]
    });
  }

  // Add these methods to load data
  loadSuppliers(): void {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });

    forkJoin([
      this.http.get<Supplier[]>(`${environment.apiUrl}/api/Suppliers/GetAll`, { headers }),
      this.http.get<SupplierLocation[]>(`${environment.apiUrl}/api/SupplierLocations/GetAll`, { headers })
    ]).subscribe({
      next: ([suppliers, locations]) => {
        this.suppliers = suppliers;
        this.supplierLocations = locations;

        // Map locations to suppliers
        this.suppliers.forEach(supplier => {
          supplier.locations = this.supplierLocations.filter(loc => loc.supplierID === supplier.supplierID);
        });
      },
      error: (error) => {
// $&
        this.showAlert('Failed to load suppliers', 'danger');
      }
    });
  }

  // Add these CRUD operations
  addSupplier(): void {
    this.isEditingSupplier = false;
    this.isEditingSupplierLocation = false;
    this.initSupplierForm(true); // Initialize with default location
    this.showSupplierModal = true;
  }



  editSupplier(supplier: Supplier): void {
    this.isEditingSupplier = true;
    this.isEditingSupplierLocation = false;

    // Initialize form without default location
    this.initSupplierForm(false);

    // Patch the supplier data
    this.supplierForm.patchValue({
      ...supplier,
      locations: []
    });

    // Add locations to form array
    const locationFormArray = this.supplierForm.get('locations') as FormArray;
    locationFormArray.clear();

    if (supplier.locations && supplier.locations.length) {
      supplier.locations.forEach(loc => {
        locationFormArray.push(this.fb.group({
          ...loc,
          // Ensure required fields have default values if they're null/undefined
          branchName: loc.branchName || 'Main Branch',
          isActive: loc.isActive !== false // Default to true if undefined
        }));
      });
    }

    this.showSupplierModal = true;
  }

  saveSupplier(): void {
    if (this.supplierForm.invalid) {
      this.supplierForm.markAllAsTouched();
      // Also mark all location form controls as touched if adding a new supplier
      if (!this.isEditingSupplier) {
        const locationsArray = this.supplierForm.get('locations') as FormArray;
        if (locationsArray && locationsArray.length > 0) {
          const firstLocation = locationsArray.at(0) as FormGroup;
          Object.keys(firstLocation.controls).forEach(key => {
            const control = firstLocation.get(key);
            if (control) {
              control.markAsTouched();
            }
          });
        }
      }
      return;
    }

    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });

    const supplierData = { ...this.supplierForm.value };
    const now = new Date().toISOString();
    supplierData.updatedAt = now;

    // Set supplierID to 0 for new suppliers and ensure locations array exists
    if (!this.isEditingSupplier) {
      supplierData.supplierID = 0;
      // Ensure the initial location has a default branch name if empty
      if (supplierData.locations && supplierData.locations.length > 0) {
        supplierData.locations[0] = {
          ...supplierData.locations[0],
          branchName: supplierData.locations[0].branchName || 'Main Branch',
          isActive: supplierData.locations[0].isActive !== false,
          locationID: 0
        };
      }
    }

    let request: Observable<any>;
    const url = `${environment.apiUrl}/api/Suppliers`;
    const isEditing = this.isEditingSupplier;

    if (isEditing) {
      request = this.http.put(`${url}/Update?SuppliersId=${supplierData.supplierID}`, supplierData, { headers });
    } else {
      supplierData.createdAt = now;
      supplierData.locations = supplierData.locations || [];
      request = this.http.post(`${url}/Add`, supplierData, { headers });
    }

    request.subscribe({
      next: (savedSupplier) => {
        this.loadSuppliers();
        if (!isEditing) {
          this.isEditingSupplier = true;
          if (savedSupplier && savedSupplier.supplierID) {
            this.supplierForm.patchValue({ supplierID: savedSupplier.supplierID });

            // Create primary branch for new supplier if provided in the form
            const initialLocation = supplierData.locations && supplierData.locations[0];
            if (initialLocation) {
              const primaryLocation = {
                locationID: 0,
                supplierID: savedSupplier.supplierID,
                branchName: initialLocation.branchName || 'Main Branch',
                addressLine1: initialLocation.addressLine1 || '',
                addressLine2: initialLocation.addressLine2 || '',
                city: initialLocation.city || '',
                region: initialLocation.region || '',
                postalCode: initialLocation.postalCode || '',
                country: initialLocation.country || '',
                locationContactPerson: initialLocation.locationContactPerson || '',
                locationEmail: initialLocation.locationEmail || '',
                locationPhone: initialLocation.locationPhone || '',
                isActive: initialLocation.isActive !== false,
                isPhysical: initialLocation.isPhysical !== false,
                createdAt: now,
                updatedAt: now
              };

              this.http.post(`${environment.apiUrl}/api/SupplierLocations/Add`, primaryLocation, { headers })
                .subscribe({
                  next: (savedLocation: any) => {
                    const locationsFA = this.supplierForm.get('locations') as FormArray;
                    if (locationsFA && locationsFA.length > 0) {
                      locationsFA.at(0).patchValue({
                        ...primaryLocation,
                        locationID: savedLocation?.locationID || 0,
                        supplierID: savedSupplier.supplierID
                      });
                    }
                    this.loadSuppliers();
                  },
                  error: (err) => {
// $&
                    this.showAlert('Supplier saved, but failed to save primary branch', 'warning');
                  }
                });
            }

            // If this was a new supplier with locations, reload the form with the saved supplier data
            if (savedSupplier.locations && savedSupplier.locations.length > 0) {
              this.editSupplier(savedSupplier);
            }
          }
        } else {
          this.closeSupplierModal();
        }
        this.showAlert(`Supplier ${isEditing ? 'updated' : 'added'} successfully`, 'success');

        // Update the locations FormArray in supplierForm to reflect the saved location
        const locationFormArray = this.supplierForm.get('locations') as FormArray;
        if (isEditing) {
          // Find and update the existing location in the array
          const index = locationFormArray.controls.findIndex(ctrl => ctrl.get('locationID')?.value === supplierData.locations[0].locationID);
          if (index !== -1) {
            locationFormArray.at(index).patchValue(supplierData.locations[0]);
          }
        } else {
          // Add the new location to the array
          if (savedSupplier && savedSupplier.locations && savedSupplier.locations.length > 0) {
            locationFormArray.push(this.fb.group(savedSupplier.locations[0]));
          }
        }
      },
      error: (error) => {
// $&
        this.showAlert('Failed to save supplier: ' + (error.error?.message || 'Unknown error'), 'danger');
      }
    });
  }

  deleteSupplier(supplier: Supplier): void {
    if (!confirm(`Are you sure you want to mark ${supplier.companyName} as inactive?`)) {
      return;
    }

    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });

    // First, get the full supplier data to ensure all required fields are included
    this.http.get(`${environment.apiUrl}/api/Suppliers/GetById?SuppliersId=${supplier.supplierID}`, { headers })
      .subscribe({
        next: (fullSupplier: any) => {
// $&
          // Update the isActive field
          fullSupplier.isActive = false;
          // Adjust property name to match backend expectation if necessary
          if (fullSupplier.supplierID !== undefined) {
            fullSupplier.SupplierID = fullSupplier.supplierID;
            delete fullSupplier.supplierID;
          }
          // Send the complete supplier data for update, ensure URL parameter matches backend
          this.http.put(`${environment.apiUrl}/api/Suppliers/Update?SuppliersId=${supplier.supplierID}`, fullSupplier, { headers })
            .subscribe({
              next: (response) => {
// $&
                this.loadSuppliers();
                this.showAlert('Supplier marked as inactive successfully', 'success');
              },
              error: (error) => {
// $&
                console.error('Error details:', JSON.stringify(error, null, 2));
                this.showAlert('Failed to mark supplier as inactive: ' + (error.error || error.message || 'Unknown error'), 'danger');
              }
            });
        },
        error: (error) => {
// $&
          console.error('Error details:', JSON.stringify(error, null, 2));
          this.showAlert('Failed to fetch supplier data: ' + (error.error || error.message || 'Unknown error'), 'danger');
        }
      });
  }

  // Supplier Location Methods
  addSupplierLocation(supplierId?: number): void {
    this.isEditingSupplierLocation = false;
    this.supplierLocationForm.reset({
      supplierID: supplierId || null,
      isActive: true,
      isPhysical: true,
      addressLine1: '',
      addressLine2: '',
      city: '',
      region: '',
      postalCode: '',
      country: '',
      locationContactPerson: '',
      locationEmail: '',
      locationPhone: ''
    });
    this.isEditingLocation = true;
  }

  editSupplierLocation(location: SupplierLocation): void {
    this.isEditingSupplierLocation = true;
    this.supplierLocationForm.patchValue({
      locationID: location.locationID,
      supplierID: location.supplierID,
      branchName: location.branchName || '',
      addressLine1: location.addressLine1 || '',
      addressLine2: location.addressLine2 || '',
      city: location.city || '',
      region: location.region || '',
      postalCode: location.postalCode || '',
      country: location.country || '',
      locationContactPerson: location.locationContactPerson || '',
      locationEmail: location.locationEmail || '',
      locationPhone: location.locationPhone || '',
      isActive: location.isActive !== false, // Default to true if undefined
      isPhysical: location.isPhysical !== false
    });
    this.isEditingLocation = true;
  }
  async generateUpdateLink(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;

    try {
      // Call our backend endpoint which will get the subscription code from the tenant's context
      const result = await firstValueFrom(
        this.http.get<any>(`${environment.apiUrl}/api/Subscription/paystack/manage-link`, {
          headers: {
            'Authorization': `Bearer ${this.authService.getToken()}`
          }
        })
      );

      if (result?.status && result.data?.link) {
        // Open the management link in a new tab
        window.open(result.data.link, '_blank');
        // Start polling provider status so UI updates (e.g., after cancellation)
        this.startProviderStatusPolling();
      } else {
        throw new Error(result?.message || 'Failed to generate management link');
      }
    } catch (error: any) {
// $&
      this.errorMessage = error.error?.message || 'Failed to generate update link. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  async cancelTrialNow(): Promise<void> {
    this.isProcessingSubscription = true;
    try {
      await firstValueFrom(this.paymentService.cancelTrial());
      this.showAlert('Success', 'Trial cancelled successfully.', 'success');
      this.loadSubscriptions();
    } catch (error: any) {
      const msg = error?.error?.message || 'Failed to cancel trial. Please try again.';
      this.showAlert('Error', msg, 'error');
    } finally {
      this.isProcessingSubscription = false;
    }
  }
  saveSupplierLocation(): void {
    if (this.supplierLocationForm.invalid) {
      this.supplierLocationForm.markAllAsTouched();
      return;
    }

    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });

    const locationData = this.supplierLocationForm.value;
    const now = new Date().toISOString();
    locationData.updatedAt = now;

    // Set locationID to 0 for new locations
    if (!this.isEditingSupplierLocation && locationData.locationID === null) {
      locationData.locationID = 0;
    }

    let request: Observable<any>;
    const url = `${environment.apiUrl}/api/SupplierLocations`;
    const isEditing = this.isEditingSupplierLocation;

    if (isEditing) {
      request = this.http.put(`${url}/Update?SupplierLocationsId=${locationData.locationID}`, locationData, { headers });
    } else {
      locationData.createdAt = now;
      request = this.http.post(`${url}/Add`, locationData, { headers });
    }

    request.subscribe({
      next: (savedLocation) => {
        this.loadSuppliers();
        this.cancelLocationEdit();
        this.showAlert(`Location ${isEditing ? 'updated' : 'added'} successfully`, 'success');

        // Update the locations FormArray in supplierForm to reflect the saved location
        const locationFormArray = this.supplierForm.get('locations') as FormArray;
        if (isEditing) {
          // Find and update the existing location in the array
          const index = locationFormArray.controls.findIndex(ctrl => ctrl.get('locationID')?.value === locationData.locationID);
          if (index !== -1) {
            locationFormArray.at(index).patchValue(locationData);
          }
        } else {
          // Add the new location to the array
          if (savedLocation && savedLocation.locationID) {
            locationData.locationID = savedLocation.locationID;
          }
          locationFormArray.push(this.fb.group(locationData));
        }
      },
      error: (error) => {
// $&
        this.showAlert('Failed to save location', 'danger');
      }
    });
  }

  deleteSupplierLocation(location: SupplierLocation): void {
    if (!confirm('Are you sure you want to delete this location?')) {
      return;
    }

    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });

    // Update maps the whole DTO onto the row, so a sparse body would blank every other
    // column — send the record back intact with only isActive flipped.
    const deactivated = { ...location, isActive: false, updatedAt: new Date().toISOString() };

    this.http.put(`${environment.apiUrl}/api/SupplierLocations/Update?SupplierLocationsId=${location.locationID}`, deactivated, { headers })
      .subscribe({
        next: () => {
          this.loadSuppliers();
          this.showAlert('Location deleted successfully', 'success');
        },
        error: (error) => {
// $&
          this.showAlert('Failed to delete location', 'danger');
        }
      });
  }

  // Role filter methods
  getAdminRoles(): Role[] {
    return this.roles.filter(role => role.isAdmin);
  }

  getWorkerRoles(): Role[] {
    return this.roles.filter(role => !role.isAdmin);
  }

  // Mobile search/filter/sort state
  mobileUserSearch = '';
  mobileUserStatus: 'all' | 'active' | 'inactive' = 'all';
  mobileUserSortAsc = true;

  mobileRoleSearch = '';
  mobileRoleSortAsc = true;

  mobileCategorySearch = '';
  mobileCategorySortAsc = true;

  mobileSupplierSearch = '';
  mobileSupplierStatus: 'all' | 'active' | 'inactive' = 'all';
  mobileSupplierSortAsc = true;

  filteredUsersMobile(): User[] {
    const q = this.mobileUserSearch.toLowerCase();
    return this.users
      .filter(u => {
        const matchSearch = !q ||
          (u.firstName + ' ' + u.lastName).toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q) ||
          (u.roleName || '').toLowerCase().includes(q);
        const matchStatus = this.mobileUserStatus === 'all' ||
          (this.mobileUserStatus === 'active' ? u.isActive : !u.isActive);
        return matchSearch && matchStatus;
      })
      .sort((a, b) => {
        const cmp = (a.firstName + ' ' + a.lastName).localeCompare(b.firstName + ' ' + b.lastName);
        return this.mobileUserSortAsc ? cmp : -cmp;
      });
  }

  filteredAdminRolesMobile(): Role[] {
    const q = this.mobileRoleSearch.toLowerCase();
    return this.getAdminRoles()
      .filter(r => !q || r.roleName.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q))
      .sort((a, b) => {
        const cmp = a.roleName.localeCompare(b.roleName);
        return this.mobileRoleSortAsc ? cmp : -cmp;
      });
  }

  filteredWorkerRolesMobile(): Role[] {
    const q = this.mobileRoleSearch.toLowerCase();
    return this.getWorkerRoles()
      .filter(r => !q || r.roleName.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q))
      .sort((a, b) => {
        const cmp = a.roleName.localeCompare(b.roleName);
        return this.mobileRoleSortAsc ? cmp : -cmp;
      });
  }

  filteredCategoriesMobile(): Category[] {
    const q = this.mobileCategorySearch.toLowerCase();
    return this.categories
      .filter(c => !q || c.categoryName.toLowerCase().includes(q))
      .sort((a, b) => {
        const cmp = a.categoryName.localeCompare(b.categoryName);
        return this.mobileCategorySortAsc ? cmp : -cmp;
      });
  }

  filteredSuppliersMobile(): any[] {
    const q = this.mobileSupplierSearch.toLowerCase();
    return this.suppliers
      .filter(s => {
        const matchSearch = !q ||
          s.companyName.toLowerCase().includes(q) ||
          (s.primaryContactPerson || '').toLowerCase().includes(q) ||
          (s.primaryEmail || '').toLowerCase().includes(q) ||
          (s.primaryPhone || '').toLowerCase().includes(q);
        const matchStatus = this.mobileSupplierStatus === 'all' ||
          (this.mobileSupplierStatus === 'active' ? s.isActive : !s.isActive);
        return matchSearch && matchStatus;
      })
      .sort((a, b) => {
        const cmp = a.companyName.localeCompare(b.companyName);
        return this.mobileSupplierSortAsc ? cmp : -cmp;
      });
  }

  // Supplier location methods
  showLocationForm(): void {
    this.addSupplierLocation(this.supplierForm.get('supplierID')?.value);
  }

  cancelLocationEdit(): void {
    this.isEditingLocation = false;
    this.isEditingSupplierLocation = false;
    this.supplierLocationForm.reset({
      isActive: true
    });
  }

  // Modal control methods
  goBack(): void {
    if (this.navigationSource === 'business-details') {
      this.router.navigate(['/business-details'], {
        queryParams: { tab: 'employees' },
        state: { from: 'config' }
      });
    } else if (this.navigationSource === 'inventory') {
      // Suppliers are a raw-material concern, so return to that page rather
      // than bouncing through the /inventory redirect onto Products.
      this.router.navigate(['/inventory/raw-materials'], {
        state: { from: 'config' }
      });
    }
  }

  closeSupplierModal(): void {
    this.supplierIsClosing = true;
    this.supplierClosingTimeoutId = setTimeout(() => {
      this.showSupplierModal = false;
      this.supplierIsClosing = false;
      this.supplierSwipeDeltaY = 0;
      this.supplierSwipeProgress = 0;
      this.supplierSwipeActive = false;
    }, 280);
    this.isEditingSupplier = false;
    this.isEditingSupplierLocation = false;
    this.supplierForm.reset();
    this.supplierLocationForm.reset();
  }

  // Mobile offcanvas state
  mobileOffcanvasVisible = false;
  mobileOffcanvasMode: ConfigOffcanvasMode = 'user';
  mobileOffcanvasItem: any = {};
  mobileOffcanvasIsEditing = false;

  showAddUserModalMobile(): void {
    this.mobileOffcanvasMode = 'user';
    this.mobileOffcanvasItem = { isActive: true };
    this.mobileOffcanvasIsEditing = false;
    this.mobileOffcanvasVisible = true;
  }

  editUserMobile(user: User): void {
    this.mobileOffcanvasMode = 'user';
    this.mobileOffcanvasItem = { ...user };
    this.mobileOffcanvasIsEditing = true;
    this.mobileOffcanvasVisible = true;
  }

  showAddRoleModalMobile(): void {
    this.mobileOffcanvasMode = 'role';
    this.mobileOffcanvasItem = { isAdmin: false, isActive: true };
    this.mobileOffcanvasIsEditing = false;
    this.mobileOffcanvasVisible = true;
  }

  editRoleMobile(role: Role): void {
    this.mobileOffcanvasMode = 'role';
    this.mobileOffcanvasItem = { ...role };
    this.mobileOffcanvasIsEditing = true;
    this.mobileOffcanvasVisible = true;
  }

  showAddCategoryModalMobile(): void {
    this.mobileOffcanvasMode = 'category';
    this.mobileOffcanvasItem = {};
    this.mobileOffcanvasIsEditing = false;
    this.mobileOffcanvasVisible = true;
  }

  editCategoryMobile(category: Category): void {
    this.mobileOffcanvasMode = 'category';
    this.mobileOffcanvasItem = { ...category };
    this.mobileOffcanvasIsEditing = true;
    this.mobileOffcanvasVisible = true;
  }

  closeMobileOffcanvas(): void {
    this.mobileOffcanvasVisible = false;
  }

  onOffcanvasSave(payload: any): void {
    if (this.mobileOffcanvasMode === 'user') {
      this.isEditingUser = this.mobileOffcanvasIsEditing;
      this.userForm.patchValue({
        userID: payload.userID ?? null,
        email: payload.email ?? '',
        firstName: payload.firstName ?? '',
        lastName: payload.lastName ?? '',
        roleID: payload.roleID ?? null,
        isActive: payload.isActive !== false,
        password: payload.password ?? ''
      });
      this.saveUser();
    } else if (this.mobileOffcanvasMode === 'role') {
      this.isEditingRole = this.mobileOffcanvasIsEditing;
      this.roleForm.patchValue({
        roleID: payload.roleID ?? null,
        roleName: payload.roleName ?? '',
        description: payload.description ?? '',
        isAdmin: payload.isAdmin ?? false,
        isActive: payload.isActive !== false,
        createdAt: payload.createdAt ?? new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      this.saveRole();
    } else if (this.mobileOffcanvasMode === 'category') {
      this.isEditingCategory = this.mobileOffcanvasIsEditing;
      const categoryId = payload.categoryID || payload.categoryId || 0;
      if (this.mobileOffcanvasIsEditing) {
        this.selectedCategory = { ...payload, categoryID: categoryId, categoryId };
      }
      this.categoryForm.patchValue({
        categoryId,
        categoryName: payload.categoryName ?? '',
        createdAt: payload.createdAt ?? new Date().toISOString()
      });
      this.saveCategory();
    }
    this.mobileOffcanvasVisible = false;
  }

  // Track the user selected for password reset
  selectedUserForReset: User | null = null;
  showResetPasswordModal = false;

  readonly Math = Math;

  // Mobile reset password bottom-sheet state
  mobileResetPwdVisible = false;
  mobileResetPwdUser: User | null = null;
  mobileResetIsClosing = false;
  mobileResetSwipeDeltaY = 0;
  mobileResetSwipeProgress = 0;
  private mobileResetSwipeStartY = 0;
  private mobileResetSwipeSheetHeight = 0;
  mobileResetSwipeActive = false;

  // Delete confirmation sheet swipe-to-dismiss state
  mobileDeleteSwipeDeltaY = 0;
  mobileDeleteSwipeProgress = 0;
  mobileDeleteSwipeActive = false;
  private mobileDeleteSwipeStartY = 0;
  private mobileDeleteSwipeSheetHeight = 0;

  // Supplier swipe-to-dismiss state
  supplierIsClosing = false;
  supplierSwipeDeltaY = 0;
  supplierSwipeProgress = 0;
  supplierSwipeStartY = 0;
  supplierSwipeSheetHeight = 0;
  supplierSwipeActive = false;

  // Open password reset confirmation modal
  resetUserPassword(user: User): void {
    if (!user?.email) {
      this.showAlert('Error', 'Invalid user email', 'error');
      return;
    }
    this.selectedUserForReset = user;
    this.showResetPasswordModal = true;
  }

  // Close the password reset modal
  closeResetPasswordModal(): void {
    this.showResetPasswordModal = false;
    this.selectedUserForReset = null;
  }

  // Mobile reset password bottom-sheet handlers
  requestResetPasswordMobile(user: User): void {
    if (!user?.email) { this.showAlert('Error', 'Invalid user email', 'error'); return; }
    this.mobileResetPwdUser = user;
    this.mobileResetPwdVisible = true;
  }

  cancelResetPasswordMobile(): void {
    this.mobileResetIsClosing = true;
    this.mobileResetClosingTimeoutId = setTimeout(() => {
      this.mobileResetPwdVisible = false;
      this.mobileResetPwdUser = null;
      this.mobileResetIsClosing = false;
    }, 280);
  }

  onDeleteSheetTouchStart(ev: TouchEvent): void {
    this.mobileDeleteSwipeStartY = ev.touches[0].clientY;
    this.mobileDeleteSwipeSheetHeight = (ev.currentTarget as HTMLElement).closest('.m-del-sheet')?.clientHeight || 300;
    this.mobileDeleteSwipeActive = true;
  }

  onDeleteSheetTouchMove(ev: TouchEvent): void {
    const delta = ev.touches[0].clientY - this.mobileDeleteSwipeStartY;
    if (delta < 0) { this.mobileDeleteSwipeDeltaY = 0; this.mobileDeleteSwipeProgress = 0; return; }
    this.mobileDeleteSwipeDeltaY = delta;
    this.mobileDeleteSwipeProgress = Math.min(1, delta / (this.mobileDeleteSwipeSheetHeight * 0.3));
  }

  onDeleteSheetTouchEnd(_ev: TouchEvent): void {
    this.mobileDeleteSwipeActive = false;
    if (this.mobileDeleteSwipeProgress >= 1) {
      this.closeDeleteModal();
      return;
    }
    this.mobileDeleteSwipeDeltaY = 0;
    this.mobileDeleteSwipeProgress = 0;
  }

  onResetSheetTouchStart(ev: TouchEvent): void {
    this.mobileResetSwipeStartY = ev.touches[0].clientY;
    this.mobileResetSwipeSheetHeight = (ev.currentTarget as HTMLElement).closest('.modal-sheet')?.clientHeight || 300;
    this.mobileResetSwipeActive = true;
  }

  onResetSheetTouchMove(ev: TouchEvent): void {
    const delta = ev.touches[0].clientY - this.mobileResetSwipeStartY;
    if (delta < 0) { this.mobileResetSwipeDeltaY = 0; this.mobileResetSwipeProgress = 0; return; }
    this.mobileResetSwipeDeltaY = delta;
    this.mobileResetSwipeProgress = Math.min(1, delta / (this.mobileResetSwipeSheetHeight * 0.3));
  }

  onResetSheetTouchEnd(_ev: TouchEvent): void {
    this.mobileResetSwipeActive = false;
    if (this.mobileResetSwipeProgress >= 1) {
      this.cancelResetPasswordMobile();
    }
    this.mobileResetSwipeDeltaY = 0;
    this.mobileResetSwipeProgress = 0;
  }

  async confirmResetPasswordMobile(): Promise<void> {
    if (!this.mobileResetPwdUser?.email) { this.showAlert('Error', 'Invalid user email', 'error'); return; }
    try {
      const token = await this.authService.getTokenAsync();
      if (!token) { this.showAlert('Error', 'Not authenticated. Please log in again.', 'error'); return; }
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const tenantId = tokenPayload?.TenantId ? parseInt(tokenPayload.TenantId, 10) : null;
      if (!tenantId) { this.showAlert('Error', 'Unable to determine tenant. Please try again later.', 'error'); return; }
      this.isLoading = true;
      this.authService.generatePasswordResetToken(this.mobileResetPwdUser.email, tenantId).pipe(first()).subscribe({
        next: () => {
          this.showAlert('Success', `Password reset link has been sent to ${this.mobileResetPwdUser?.email}`, 'success');
          this.isLoading = false;
          this.cancelResetPasswordMobile();
        },
        error: (error: any) => {
          const errorMessage = error.error?.message || 'Failed to send password reset email. Please try again.';
          this.showAlert('Error', errorMessage, 'error');
          this.isLoading = false;
        }
      });
    } catch {
      this.showAlert('Error', 'An unexpected error occurred. Please try again.', 'error');
      this.isLoading = false;
      this.cancelResetPasswordMobile();
    }
  }

  // Supplier swipe-to-dismiss handlers
  onSupplierSheetTouchStart(ev: TouchEvent): void {
    this.supplierSwipeStartY = ev.touches[0].clientY;
    this.supplierSwipeSheetHeight = (ev.currentTarget as HTMLElement).closest('.offcanvas-sheet')?.clientHeight || window.innerHeight * 0.9;
    this.supplierSwipeActive = true;
  }

  onSupplierSheetTouchMove(ev: TouchEvent): void {
    const delta = ev.touches[0].clientY - this.supplierSwipeStartY;
    if (delta < 0) { this.supplierSwipeDeltaY = 0; this.supplierSwipeProgress = 0; return; }
    this.supplierSwipeDeltaY = delta;
    this.supplierSwipeProgress = Math.min(delta / this.supplierSwipeSheetHeight, 1);
  }

  onSupplierSheetTouchEnd(_ev: TouchEvent): void {
    this.supplierSwipeActive = false;
    if (this.supplierSwipeProgress > 0.3) {
      this.closeSupplierModal();
    }
    this.supplierSwipeDeltaY = 0;
    this.supplierSwipeProgress = 0;
  }

  formatSupplierLocations(supplier: any): string {
    const locs = supplier?.locations ?? [];
    if (!locs.length) return 'No locations';
    return locs
      .map((l: any) => `${l.branchName || 'Main'} (${l.city || 'N/A'}) · ${l.isPhysical !== false ? 'Physical' : 'Postal'}`)
      .join(', ');
  }

  // Confirm and process password reset
  async confirmResetPassword(): Promise<void> {
    if (!this.selectedUserForReset?.email) {
      this.showAlert('Error', 'Invalid user email', 'error');
      return;
    }

    try {
      // Get the current user's token
      const token = await this.authService.getTokenAsync();
      if (!token) {
        this.showAlert('Error', 'Not authenticated. Please log in again.', 'error');
        return;
      }

      // Decode the token to get the tenant ID
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const tenantId = tokenPayload?.TenantId ? parseInt(tokenPayload.TenantId, 10) : null;

      if (!tenantId) {
        this.showAlert('Error', 'Unable to determine tenant. Please try again later.', 'error');
        return;
      }

      this.isLoading = true;
      // Using the authService's generatePasswordResetToken method with tenant ID
      this.authService.generatePasswordResetToken(this.selectedUserForReset.email, tenantId).pipe(first()).subscribe({
        next: () => {
          this.showAlert('Success', `Password reset link has been sent to ${this.selectedUserForReset?.email}`, 'success');
          this.isLoading = false;
          this.closeResetPasswordModal();
        },
        error: (error) => {
          console.error('Error sending password reset email:', error);
          const errorMessage = error.error?.message || 'Failed to send password reset email. Please try again.';
          this.showAlert('Error', errorMessage, 'error');
          this.isLoading = false;
        }
      });
    } catch (error) {
      console.error('Error processing password reset:', error);
      this.showAlert('Error', 'An unexpected error occurred. Please try again.', 'error');
      this.isLoading = false;
      this.closeResetPasswordModal();
    }
  }
}
